import React, { useState } from 'react'
import { Trans, useTranslation } from 'next-i18next'
import styled from 'styled-components'
import { useDialogController } from '@machobear/design-system'
import { FormikHelpers as FormikActions, FormikProps } from 'formik'

import { breakpoints, colors, fontWeights } from '@/styles'
import useMediaQuery from '@/hooks/use-media-query'
import usePasswordRequirements from '@/hooks/use-password-requirements'
import { PasswordRequirements } from '@/features/Auth/components'
import { useDSLogin } from '@/features/Auth/Context/ds-login-provider'
import DSLoginModal from '@/features/Auth/login-modal/ds-login-modal'
import { FieldInput, Group, PasswordInput } from '@/components/form'
import { LoadingDotCircle as Loading } from '@/components/loading-circle'

import { Listing } from '../../types/Listing'
import AnimatedText from '../AnimatedText'

const Link = styled.a<{ ul?: boolean }>`
  color: ${colors.link};
  font-weight: ${fontWeights.regular};
  text-decoration: none;
  ${props => props.ul && `border-bottom: 1px solid ${colors.link};`}
`

const Terms = styled(Group)`
  font-size: 12px;
  line-height: 16px;
  margin-top: -10px;
  color: ${colors.grey32};
`

const LoadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 0 0 30px 0;
`

type Props = {
  formikProps: FormikProps<Listing>
  createListing: (
    values: Listing,
    reset: FormikActions<Listing>['resetForm'],
    userId: string
  ) => Promise<(() => void) | undefined>
}

const Signup = ({ formikProps, createListing }: Props) => {
  const { errors, values, setFieldValue, isSubmitting, resetForm } = formikProps

  const [showPassword, setShowPassword] = useState(false)
  const { requirements, checkRequirements } = usePasswordRequirements()
  const [passwordFocused, setPasswordFocused] = useState(false)
  const { visible, showModal, closeModal } = useDialogController()
  const { setAfterLogin } = useDSLogin()

  const { t } = useTranslation()

  const displayMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)

  const mobileGapSize = displayMobile ? 16 : '0'

  return isSubmitting ? (
    <LoadingWrapper>
      <Loading />
      <AnimatedText text={'Creating Landlord'} />
    </LoadingWrapper>
  ) : (
    <>
      <Group col={12} bottom={mobileGapSize}>
        <div style={{ cursor: 'pointer', marginTop: '-30px' }}>
          <Trans i18nKey="b.auth.create.already_have_an_account.text">
            Already have an account?{' '}
            <Link
              onClick={() => {
                setAfterLogin(session => {
                  if (session?.id) {
                    createListing(values, resetForm, session.id)
                  }
                })
                showModal()
              }}
            >
              Log in here
            </Link>
            .
          </Trans>
        </div>
      </Group>

      <Group col={12} bottom={mobileGapSize}>
        <FieldInput
          name="full_name"
          autoComplete="section-register-landlord given-name"
          id="full_name"
          required
          placeholder={t('b.auth.create.full_name.label', 'Full name')}
        />
      </Group>

      <Group col={12} bottom={mobileGapSize}>
        <FieldInput
          name="email"
          autoComplete="section-register-landlord username email"
          id="email"
          type="email"
          required
          placeholder={t('common.email', 'Email')}
        />
      </Group>

      <Group col={12} bottom={errors?.password ? 5 : '0'}>
        <PasswordInput
          name="password"
          required
          id="password"
          placeholder={t(
            'b.auth.create.enter_password.label',
            'Enter password'
          )}
          onChange={e => {
            setFieldValue('password', e.target.value),
              checkRequirements(e.target.value)
          }}
          visible={showPassword}
          setVisible={setShowPassword}
          onFocus={() => setPasswordFocused(true)}
          onBlur={() => setPasswordFocused(false)}
          hideError={passwordFocused}
        />
      </Group>

      <Group col={12} bottom={mobileGapSize}>
        <PasswordRequirements
          requirements={requirements}
          show={passwordFocused}
        />
      </Group>

      <Terms>
        <span>
          <Trans i18nKey="b.auth.create.create_listing_signup.text" t={t}>
            By clicking &quot;Continue&quot;, I confirm that I have read and
            agree to liv.rent
            {`'`}s{' '}
            <Link href="/terms" target="_blank" ul>
              Terms & Conditions
            </Link>{' '}
            and{' '}
            <Link href="/privacy" target="_blank" ul>
              Privacy Policy
            </Link>
            .
          </Trans>
        </span>
      </Terms>

      <DSLoginModal
        mode="login"
        eventId="web-signup-submit-post-listing"
        visible={visible}
        onClose={closeModal}
        t={t}
        defaultOperationType={'landlord' as any}
        autoFocusName={false}
      />
    </>
  )
}

export default Signup
